/* KD9 — /app/agenda (User) · Pantalla 4 · Seguimiento de solicitudes de Acompañamiento.
   Reutiliza GET /api/acompanamiento/mine. Ningún endpoint nuevo.
   NO muestra: private_notes del consultor, admin_observation, ni información reservada. */
import React, { useCallback, useEffect, useState } from "react";
import { PageShell } from "@/pages/dashboard/RolePages";
import {
  Calendar, Clock, User, FileText, Video, MessageSquare, Phone,
  CheckCircle2, Ticket, ChevronDown, ChevronUp, Download, Heart,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/* ---------- meta helpers ---------- */
const STATUS_META = {
  pending_assignment:  { label: "Pendiente de asignación",  Icon: Clock,        c: "var(--kd9-gold)", bg: "rgba(212,175,55,0.16)", bd: "rgba(212,175,55,0.40)" },
  assigned:            { label: "Consultor asignado",       Icon: User,         c: "#7BB8E8",          bg: "rgba(15,76,129,0.20)",  bd: "rgba(15,76,129,0.50)" },
  consultor_accepted:  { label: "Consultor aceptó",         Icon: CheckCircle2, c: "#A8D8B9",          bg: "rgba(31,167,116,0.18)", bd: "rgba(31,167,116,0.40)" },
  scheduled:           { label: "Cita programada",          Icon: Calendar,     c: "#C0A555",          bg: "rgba(166,124,0,0.18)",  bd: "rgba(166,124,0,0.50)" },
  completed:           { label: "Atención finalizada",      Icon: CheckCircle2, c: "#86EFAC",          bg: "rgba(34,197,94,0.14)",  bd: "rgba(34,197,94,0.40)" },
};

const MEDIUM_META = {
  whatsapp: { label: "WhatsApp",              Icon: MessageSquare, contactKey: "whatsapp",  contactLabel: "Usuario / enlace WhatsApp" },
  zoom:     { label: "Zoom",                  Icon: Video,         contactKey: "zoom_link", contactLabel: "Enlace Zoom" },
  meet:     { label: "Google Meet",           Icon: Video,         contactKey: "meet_link", contactLabel: "Enlace Google Meet" },
  phone:    { label: "Llamada telefónica",    Icon: Phone,         contactKey: "phone",     contactLabel: "Número telefónico" },
};

const FINAL_LABEL = {
  attention_completed: "Atención completada",
  not_attended: "No asistió",
  rescheduled: "Reagendado",
};

const photoSrc = (value) => value && !/^https?:\/\//.test(value) ? (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + value : value;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-AR", { year: "numeric", month: "short", day: "2-digit" }) : "—";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDT   = (iso) => iso ? `${fmtDate(iso)} · ${fmtTime(iso)}` : "—";

/* Build a canonical timeline from history[] entries — user-safe (no internal fields). */
function buildTimeline(row) {
  const events = [];
  events.push({ at: row.created_at, action: "created", label: "Solicitud creada" });
  events.push({ at: row.created_at, action: "pending", label: "Pendiente de asignación" });
  for (const h of row.history || []) {
    if (!h || !h.action) continue;
    if (h.action === "created") continue; // already listed
    const label = ({
      assigned: "Consultor asignado",
      consultor_accepted: "Consultor aceptó",
      scheduled: h.scheduled_date ? `Cita programada · ${h.scheduled_date} ${h.scheduled_time || ""}` : "Cita programada",
      completed: h.final_status ? `Atención finalizada · ${FINAL_LABEL[h.final_status] || h.final_status}` : "Atención finalizada",
    })[h.action] || h.action;
    events.push({ at: h.at, action: h.action, label });
  }
  // Sort by timestamp ascending
  return events.sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));
}

const MiAgenda = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/api/acompanamiento/mine");
      setRows(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = filterStatus === "all" ? rows : rows.filter((r) => r.status === filterStatus);

  return (
    <PageShell
      kicker="Usuario · Mi agenda"
      title="SEGUIMIENTO DE MIS SESIONES"
      desc="Aquí ves el estado de tus solicitudes de acompañamiento. Cuando el consultor programe la sesión te mostraremos los datos de contacto para el medio elegido."
      testId="mi-agenda-page"
      badge={<span className="kd9-card px-4 py-2 text-[11px] font-mont tracking-widest uppercase" data-testid="agenda-count">{filtered.length} sesion{filtered.length === 1 ? "" : "es"}</span>}
    >
      {/* Filtros */}
      <div className="kd9-card p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="agenda-filters">
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
          <select className="kd9-input pl-3 py-2 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="agenda-filter-status">
            <option value="all">Todas</option>
            <option value="pending_assignment">Pendiente de asignación</option>
            <option value="assigned">Consultor asignado</option>
            <option value="consultor_accepted">Consultor aceptó</option>
            <option value="scheduled">Cita programada</option>
            <option value="completed">Atención finalizada</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont" data-testid="agenda-error"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}>{error}</div>
      )}

      {loading ? (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }} data-testid="agenda-loading">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="kd9-card p-10 text-center" data-testid="agenda-empty">
          <Heart size={28} className="mx-auto mb-3" style={{ color: "var(--kd9-gold)" }} />
          <div className="font-cinzel kd9-gold-text text-lg tracking-widest mb-2">SIN SOLICITUDES</div>
          <p className="font-cormorant text-base" style={{ color: "var(--kd9-text-soft)" }}>
            Aún no has solicitado acompañamiento con este filtro. Comenzá desde el módulo Acompañamiento.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="agenda-list">
          {filtered.map((r, i) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending_assignment;
            const isOpen = openId === r.id;
            const isScheduledOrLater = ["scheduled", "completed"].includes(r.status);
            const mMeta = r.medium ? MEDIUM_META[r.medium] : null;
            const contactValue = mMeta && r.contact_info ? r.contact_info[mMeta.contactKey] : null;
            const timeline = buildTimeline(r);
            return (
              <div key={r.id} className="kd9-card overflow-hidden" data-testid={`agenda-row-${i}`}>
                {/* Header */}
                <div className="p-5 grid grid-cols-12 gap-4 items-center border-b" style={{ borderColor: "var(--kd9-border)" }}>
                  <div className="col-span-12 md:col-span-4">
                    <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Solicitud</div>
                    <div className="font-cinzel kd9-gold-text text-lg tracking-wider" data-testid={`agenda-id-${i}`}>#{r.id.slice(-8).toUpperCase()}</div>
                    <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }} data-testid={`agenda-created-${i}`}>Creada: {fmtDT(r.created_at)}</div>
                  </div>
                  <div className="col-span-6 md:col-span-3 font-mont text-sm">
                    <div className="text-[10px] tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Sesión</div>
                    <div data-testid={`agenda-type-${i}`}>{r.session_type === "phone" ? "Llamada telefónica" : "Videollamada externa"}</div>
                    <div style={{ color: "var(--kd9-text-soft)" }} data-testid={`agenda-duration-${i}`}>{r.duration_min} minutos</div>
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <div className="text-[10px] tracking-widest uppercase font-mont" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
                    <span className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-mont tracking-widest uppercase font-semibold" data-testid={`agenda-status-${i}`}
                      style={{ background: meta.bg, color: meta.c, border: `1px solid ${meta.bd}` }}>
                      <meta.Icon size={12} /> {meta.label}
                    </span>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex justify-end">
                    <button onClick={() => setOpenId(isOpen ? null : r.id)} className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2" data-testid={`agenda-toggle-${i}`}>
                      {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {isOpen ? "Cerrar" : "Ver detalle"}
                    </button>
                  </div>
                </div>

                {/* Body */}
                {isOpen && (
                  <div className="p-5 grid md:grid-cols-2 gap-6" data-testid={`agenda-panel-${i}`}>
                    {/* Left column: motivo, consultor, contacto */}
                    <div>
                      <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Motivo de consulta</div>
                      <p className="font-cormorant text-base mb-4" data-testid={`agenda-reason-${i}`}>{r.reason}</p>

                      {r.notes && (
                        <>
                          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Mis observaciones</div>
                          <p className="font-cormorant text-base mb-4" data-testid={`agenda-user-notes-${i}`}>{r.notes}</p>
                        </>
                      )}

                      {/* Consultor asignado (cuando exista) */}
                      {r.assigned_consultor_email && (
                        <div className="kd9-card p-4 mb-4" data-testid={`agenda-consultor-${i}`}
                          style={{ background: "rgba(15,76,129,0.08)", borderColor: "rgba(15,76,129,0.35)" }}>
                          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Consultor asignado</div>
                          {r.assigned_consultor_photo_url && <img src={photoSrc(r.assigned_consultor_photo_url)} alt={r.assigned_consultor_name || "Consultor KD9"} className="w-20 h-20 rounded-xl object-cover mb-3" style={{ border: "1px solid var(--kd9-gold)" }} />}
                          <div className="font-mont font-semibold text-sm" data-testid={`agenda-consultor-name-${i}`}>{r.assigned_consultor_name || r.assigned_consultor_email}</div>
                          <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }} data-testid={`agenda-consultor-email-${i}`}>{r.assigned_consultor_email}</div>
                          {r.assigned_at && (
                            <div className="text-[11px] font-mont mt-2" style={{ color: "var(--kd9-text-soft)" }}>
                              Asignación: {fmtDT(r.assigned_at)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Cita programada + datos de contacto (solo cuando scheduled o completed) */}
                      {isScheduledOrLater && r.scheduled_date && (
                        <div className="kd9-card p-4 mb-4" data-testid={`agenda-appointment-${i}`}
                          style={{ background: "rgba(212,175,55,0.08)", borderColor: "rgba(212,175,55,0.40)" }}>
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar size={14} className="kd9-gold-text" />
                            <div className="font-cinzel kd9-gold-text text-sm tracking-widest">CITA PROGRAMADA</div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Fecha</div>
                              <div className="font-mont text-sm font-semibold" data-testid={`agenda-scheduled-date-${i}`}>{r.scheduled_date}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Hora</div>
                              <div className="font-mont text-sm font-semibold" data-testid={`agenda-scheduled-time-${i}`}>{r.scheduled_time}</div>
                            </div>
                          </div>
                          {mMeta && (
                            <div className="mb-3">
                              <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Medio de atención</div>
                              <div className="inline-flex items-center gap-2 font-mont text-sm font-semibold" data-testid={`agenda-medium-${i}`}>
                                <mMeta.Icon size={14} className="kd9-gold-text" /> {mMeta.label}
                              </div>
                            </div>
                          )}
                          {mMeta && contactValue ? (
                            <div>
                              <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>{mMeta.contactLabel}</div>
                              {(r.medium === "zoom" || r.medium === "meet") ? (
                                <a href={contactValue} target="_blank" rel="noreferrer" className="kd9-gold-text font-mont text-sm underline break-all" data-testid={`agenda-contact-${i}`}>{contactValue}</a>
                              ) : (
                                <div className="font-mont text-sm break-all" data-testid={`agenda-contact-${i}`}>{contactValue}</div>
                              )}
                            </div>
                          ) : (
                            <div className="text-[11px] font-mont italic" style={{ color: "var(--kd9-text-soft)" }} data-testid={`agenda-contact-empty-${i}`}>
                              El consultor aún no cargó los datos de contacto para el medio elegido.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Adjunto del usuario (siempre) */}
                      {r.attachment_url && (
                        <div className="mb-4">
                          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Mi archivo adjunto</div>
                          <a href={r.attachment_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 kd9-gold-text font-mont text-sm underline break-all" data-testid={`agenda-my-attachment-${i}`}>
                            <FileText size={12} /> {r.attachment_url}
                          </a>
                        </div>
                      )}

                      {/* Documentos del consultor (recomendaciones + observaciones terapéuticas) — SOLO si completed */}
                      {r.status === "completed" && (r.therapy_notes || r.recommendations) && (
                        <div className="kd9-card p-4" data-testid={`agenda-docs-${i}`}
                          style={{ background: "rgba(34,197,94,0.06)", borderColor: "rgba(34,197,94,0.35)" }}>
                          <div className="flex items-center gap-2 mb-3">
                            <Download size={14} style={{ color: "#86EFAC" }} />
                            <div className="font-cinzel text-sm tracking-widest" style={{ color: "#86EFAC" }}>DOCUMENTOS FINALES</div>
                          </div>
                          {r.therapy_notes && (
                            <div className="mb-4">
                              <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observaciones terapéuticas</div>
                              <p className="font-cormorant text-base whitespace-pre-line" data-testid={`agenda-therapy-notes-${i}`}>{r.therapy_notes}</p>
                            </div>
                          )}
                          {r.recommendations && (
                            <div className="mb-3">
                              <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Recomendaciones</div>
                              <p className="font-cormorant text-base whitespace-pre-line" data-testid={`agenda-recommendations-${i}`}>{r.recommendations}</p>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const parts = [];
                              parts.push(`KD9 — Documento final · Solicitud #${r.id.slice(-8).toUpperCase()}`);
                              parts.push(`Fecha de sesión: ${r.scheduled_date || "—"} ${r.scheduled_time || ""}`);
                              parts.push(`Consultor: ${r.assigned_consultor_name || r.assigned_consultor_email || "—"}`);
                              parts.push("");
                              if (r.therapy_notes) { parts.push("== Observaciones terapéuticas =="); parts.push(r.therapy_notes); parts.push(""); }
                              if (r.recommendations) { parts.push("== Recomendaciones =="); parts.push(r.recommendations); parts.push(""); }
                              const blob = new Blob([parts.join("\n")], { type: "text/plain;charset=utf-8" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `kd9-acompanamiento-${r.id.slice(-8)}.txt`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            }}
                            className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] inline-flex items-center gap-2"
                            data-testid={`agenda-download-${i}`}
                          >
                            <Download size={12} /> Descargar documento (.txt)
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Right column: timeline */}
                    <div>
                      <div className="text-[10px] font-mont tracking-widest uppercase mb-3" style={{ color: "var(--kd9-text-soft)" }}>Timeline</div>
                      <ol className="relative border-l pl-4 space-y-4" style={{ borderColor: "var(--kd9-border)" }} data-testid={`agenda-timeline-${i}`}>
                        {timeline.map((ev, k) => (
                          <li key={k} className="relative" data-testid={`agenda-timeline-event-${i}-${k}`}>
                            <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full"
                              style={{ background: ev.action === (timeline[timeline.length - 1]?.action) ? "var(--kd9-gold)" : "var(--kd9-border)", border: "2px solid var(--kd9-bg-2)" }} />
                            <div className="font-mont text-sm font-semibold">{ev.label}</div>
                            <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                              {fmtDate(ev.at)} · {fmtTime(ev.at)}
                            </div>
                          </li>
                        ))}
                      </ol>

                      {r.status === "completed" && r.final_status && (
                        <div className="mt-6 p-4 rounded-lg border" data-testid={`agenda-final-${i}`}
                          style={{ background: "rgba(34,197,94,0.06)", borderColor: "rgba(34,197,94,0.35)" }}>
                          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Cierre</div>
                          <div className="font-mont text-sm font-semibold" style={{ color: "#86EFAC" }}>
                            {FINAL_LABEL[r.final_status] || r.final_status}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
};

export default MiAgenda;
